import store from '@/store'
import moment from 'moment'
import { ref } from '@vue/composition-api'

/**
 * Business Metrics Helper - SINGLE SOURCE OF TRUTH
 * 
 * Centralized business logic for Lagermeister rental management system.
 * This helper consolidates logic from DashboardTiles.js and other scattered calculations.
 * This helper ensures consistency across all components and dashboards.
 * 
 * CRITICAL: Always use this helper for business calculations.
 * Never duplicate logic in individual components.
 * 
 * Migrated from DashboardTiles.js to eliminate 340+ lines of duplicated business logic.
 */
export default function businessMetrics() {
  /**
   * Core Business Rule: Active Rental Detection
   * An order item represents an active rental if moveOutDate is null
   */
  const isActiveRental = orderItem => orderItem && orderItem.moveOutDate === null

  /**
   * Get all active rentals from order items array
   */
  const getActiveRentals = orderItems => {
    if (!orderItems || !Array.isArray(orderItems)) return []
    return orderItems.filter(isActiveRental)
  }

  /**
   * Calculate Monthly Recurring Revenue (MRR)
   * Sum of all active rental monthly prices
   */
  const calculateMRR = orderItems => {
    const activeRentals = getActiveRentals(orderItems)
    return activeRentals.reduce((total, item) => total + (item.price || 0), 0)
  }

  /**
   * Calculate current MRR from store
   */
  const getCurrentMRR = () => {
    const orderItems = store.getters.orderItemsEntries || []
    return calculateMRR(orderItems)
  }

  /**
   * Get count of active rentals
   */
  const getActiveRentalsCount = orderItems => getActiveRentals(orderItems).length

  /**
   * Get current active rentals count from store
   */
  const getCurrentActiveRentalsCount = () => {
    const orderItems = store.getters.orderItemsEntries || []
    return getActiveRentalsCount(orderItems)
  }

  /**
   * Calculate average monthly rate per active rental
   */
  const calculateAverageMonthlyRate = orderItems => {
    const activeRentals = getActiveRentals(orderItems)
    if (activeRentals.length === 0) return 0
    
    const totalMRR = calculateMRR(orderItems)
    return totalMRR / activeRentals.length
  }

  /**
   * Get current average monthly rate from store
   */
  const getCurrentAverageMonthlyRate = () => {
    const orderItems = store.getters.orderItemsEntries || []
    return calculateAverageMonthlyRate(orderItems)
  }

  /**
   * Check if a storage unit has active rentals
   */
  const hasActiveRental = storage => {
    if (!storage || !storage.order_items || !Array.isArray(storage.order_items)) {
      return false
    }
    return storage.order_items.some(isActiveRental)
  }

  /**
   * Get MRR for a specific location
   */
  const calculateLocationMRR = locationId => {
    const orderItems = store.getters.orderItemsEntries || []
    const storages = store.getters.storageEntries || []
    
    // Get storage IDs for this location
    const locationStorageIds = storages
      .filter(storage => storage.location && storage.location.id === locationId)
      .map(storage => storage.id)
    
    // Filter order items for this location's storages
    const locationOrderItems = orderItems.filter(orderItem => locationStorageIds.includes(orderItem.storage_id)
      || (orderItem.storage && locationStorageIds.includes(orderItem.storage.id)))
    
    return calculateMRR(locationOrderItems)
  }

  /**
   * Get total revenue from all order items (from DashboardTiles.js)
   * Uses store getter for accumulated revenue calculation
   */
  const getRevenueTillNow = () => {
    const totalRevenue = store.getters.orderItemsTotalSumFromStartToNow || 0
    return `${totalRevenue.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`
  }

  /**
   * Get current month revenue (from DashboardTiles.js)
   */
  const getCurrentMonthRevenue = () => {
    const monthlyRevenue = store.getters.orderItemsTotalSumForCurrentMonth || 0
    return `${monthlyRevenue.toLocaleString('de-DE')} €`
  }

  /**
   * Get monthly revenue data for charts (from DashboardTiles.js)
   */
  const getOrderItemsMonthlyRevenue = () => store.getters.orderItemsMonthlyRevenue || {}

  /**
   * Get current user (from DashboardTiles.js)
   */
  const getCurrentUser = () => store.getters.user

  /**
   * Get accepted inquiries count (from DashboardTiles.js)
   */
  const getAcceptedInquiries = () => store.getters.inquiriesAllAcceptedEntries?.length || 0

  /**
   * Get open inquiries count (from DashboardTiles.js)
   */
  const getOpenInquiries = () => store.getters.inquiriesAllOpenEntries?.length || 0

  /**
   * Get total sum of all storages and offers (from DashboardTiles.js)
   */
  const getTotalSumOfAllStoragesAndOffers = () => {
    const total = store.getters.getTotalSumOfAllStoragesAndOffers || 0
    return `${total.toLocaleString('de-DE')} €`
  }

  /**
   * Get active offers count (from DashboardTiles.js)
   */
  const getActiveOffers = () => store.getters.getActiveOffers?.length || 0

  /**
   * Get count of all order items (from DashboardTiles.js)
   */
  const getCountOfAllOrderItems = () => store.getters.orderItemsEntries?.length || 0

  /**
   * Get sum of all active order items for current month (from DashboardTiles.js)
   */
  const getSumOfAllActiveOrderItemsForCurrentMonth = () => {
    const monthlySum = store.getters.orderItemsTotalSumForCurrentMonth || 0
    return `${monthlySum.toLocaleString('de-DE')} €`
  }

  /**
   * Get sum of all active order items (from DashboardTiles.js)
   */
  const getSumOfAllActiveOrderItems = () => {
    const totalSum = store.getters.orderItemsTotalSum || 0
    return `${totalSum.toLocaleString('de-DE')} €`
  }

  /**
   * Get sum of all active order items till today (from DashboardTiles.js)
   */
  const getSumOfAllActiveOrderItemsTillToday = () => {
    const totalSum = store.getters.orderItemsTotalSumFromStartToNow || 0
    return `${totalSum.toLocaleString('de-DE')} €`
  }

  /**
   * Get sum of all active order items till moveout date (from DashboardTiles.js)
   */
  const getSumOfAllActiveOrderItemsTillMoveoutdate = () => {
    const totalSum = store.getters.orderItemsTotalSumFromStartToMoveOutDate || 0
    return `${totalSum.toLocaleString('de-DE')} €`
  }

  /**
   * Get storage utilization trends over last 12 months (from DashboardTiles.js)
   */
  const getStorageUtilizationTrends = () => {
    const utilizationData = {}
    const totalStorages = store.getters.storageEntries?.length || 0
    
    if (totalStorages === 0) return {}

    // Generate last 12 months data
    for (let i = 11; i >= 0; i -= 1) {
      const date = moment().subtract(i, 'months')
      const monthKey = date.format('YYYY-MM')
      const monthLabel = date.format('MMM', 'de')
      
      let occupiedCount = 0

      // Check each storage's occupancy for this month
      const storages = store.getters.storageEntries || []
      storages.forEach(storage => {
        if (storage.order_items && storage.order_items.length > 0) {
          // Check if storage was occupied during this month
          const wasOccupied = storage.order_items.some(orderItem => {
            const rentStart = moment(orderItem.rentStart).format('YYYY-MM')
            const moveOut = orderItem.moveOutDate 
              ? moment(orderItem.moveOutDate).format('YYYY-MM')
              : null
            
            // Storage was occupied if:
            // - Rent started before or during this month AND
            // - Move out is null (still active) OR move out is after this month
            return rentStart <= monthKey && (!moveOut || moveOut >= monthKey)
          })
          
          if (wasOccupied) {
            occupiedCount += 1
          }
        }
      })

      const utilizationPercentage = Math.round((occupiedCount / totalStorages) * 100)
      
      utilizationData[monthLabel] = {
        percentage: utilizationPercentage,
        occupied: occupiedCount,
        total: totalStorages,
        available: totalStorages - occupiedCount
      }
    }

    return utilizationData
  }

  /**
   * Get business funnel data for conversion analysis (from DashboardTiles.js)
   */
  const getBusinessFunnelData = () => {
    const inquiriesCount = store.getters.inquiriesAllEntries?.length || 0
    const offersCount = store.getters.offerEntries?.length || 0
    const ordersCount = store.getters.orderEntries?.length || 0
    const orderItems = store.getters.orderItemsEntries || []
    const activeRentalsCount = getActiveRentalsCount(orderItems)

    // Calculate conversion rates
    const inquiryToOffer = inquiriesCount > 0 ? Math.round((offersCount / inquiriesCount) * 100) : 0
    const offerToOrder = offersCount > 0 ? Math.round((ordersCount / offersCount) * 100) : 0
    const orderToRental = ordersCount > 0 ? Math.round((activeRentalsCount / ordersCount) * 100) : 0

    return {
      stages: [
        { name: 'Anfragen', count: inquiriesCount, conversion: 100 },
        { name: 'Angebote', count: offersCount, conversion: inquiryToOffer },
        { name: 'Buchungen', count: ordersCount, conversion: offerToOrder },
        { name: 'Aktive Mieten', count: activeRentalsCount, conversion: orderToRental }
      ],
      totalConversion: inquiriesCount > 0 ? Math.round((activeRentalsCount / inquiriesCount) * 100) : 0
    }
  }

  /**
   * Get count functions (from DashboardTiles.js)
   */
  const getCountOfAllStorages = () => store.getters.storageEntries?.length || 0
  const getCountOfAllOrders = () => store.getters.orderEntries?.length || 0
  const getCountOfAllInquiries = () => store.getters.inquiriesAllEntries?.length || 0
  const getCountOfAllOffers = () => store.getters.offerEntries?.length || 0

  /**
   * Storage availability functions (from storageHelper.js and DashboardTiles.js)
   */
  const getFreeStorages = () => {
    const storages = store.getters.storageEntries || []
    const orderItems = store.getters.orderItemsEntries || []
    
    // Create a Set of storage IDs that have active rentals for performance
    const activeStorageIds = new Set()
    orderItems.forEach(orderItem => {
      if (!orderItem.moveOutDate) {
        if (orderItem.storage_id) {
          activeStorageIds.add(orderItem.storage_id)
        } else if (orderItem.storage && orderItem.storage.id) {
          activeStorageIds.add(orderItem.storage.id)
        }
      }
    })

    // Count storages that are neither reserved nor have active rentals
    let freeStorages = 0
    storages.forEach(storage => {
      if (storage.storageReservation !== true && !activeStorageIds.has(storage.id)) {
        freeStorages += 1
      }
    })

    return freeStorages
  }

  const getRentedStorages = () => {
    const storages = store.getters.storageEntries || []
    const orderItems = store.getters.orderItemsEntries || []
    
    // Create a Set of storage IDs that have active rentals for performance
    const activeStorageIds = new Set()
    orderItems.forEach(orderItem => {
      if (!orderItem.moveOutDate) {
        if (orderItem.storage_id) {
          activeStorageIds.add(orderItem.storage_id)
        } else if (orderItem.storage && orderItem.storage.id) {
          activeStorageIds.add(orderItem.storage.id)
        }
      }
    })

    // Count storages that are either reserved or have active rentals
    let rentedStorages = 0
    storages.forEach(storage => {
      if (storage.storageReservation === true || activeStorageIds.has(storage.id)) {
        rentedStorages += 1
      }
    })

    return rentedStorages
  }

  /**
   * Get location performance comparison data (from DashboardTiles.js)
   */
  const getLocationPerformanceData = (period = 'current') => {
    const locations = store.getters.locationsEntries || []
    const storages = store.getters.storageEntries || []
    const orderItems = store.getters.orderItemsEntries || []
    
    // Calculate date range based on selected period
    let startDate
    let endDate
    const now = moment()
    
    switch (period) {
      case 'last3months':
        startDate = moment().subtract(3, 'months').startOf('month')
        endDate = now
        break
      case 'last6months':
        startDate = moment().subtract(6, 'months').startOf('month')
        endDate = now
        break
      case 'last12months':
        startDate = moment().subtract(12, 'months').startOf('month')
        endDate = now
        break
      default: // current month
        startDate = moment().startOf('month')
        endDate = now
        break
    }

    const locationStats = locations.map(location => {
      // Get storages for this location
      const locationStorages = storages.filter(storage => storage.location && storage.location.id === location.id)
      const totalStorages = locationStorages.length

      // Calculate revenue and occupancy for this location
      let revenue = 0
      let activeRentals = 0

      // Get order items for all storages in this location
      const storageIds = locationStorages.map(storage => storage.id)
      const locationOrderItems = orderItems.filter(orderItem => storageIds.includes(orderItem.storage_id) 
               || (orderItem.storage && storageIds.includes(orderItem.storage.id)))

      locationOrderItems.forEach(orderItem => {
        const rentStart = moment(orderItem.rentStart)
        const moveOut = orderItem.moveOutDate ? moment(orderItem.moveOutDate) : null

        // Check if order item is active during the selected period
        const isActiveInPeriod = rentStart.isBefore(endDate) 
                                 && (!moveOut || moveOut.isAfter(startDate))

        if (isActiveInPeriod) {
          // Calculate months in period
          const effectiveStart = moment.max(rentStart, startDate)
          const effectiveEnd = moveOut ? moment.min(moveOut, endDate) : endDate
          const monthsInPeriod = effectiveEnd.diff(effectiveStart, 'months', true)
          
          revenue += Math.ceil(monthsInPeriod) * (orderItem.price || 0)
          
          // Count as active rental if currently active
          if (!moveOut || moveOut.isAfter(now)) {
            activeRentals += 1
          }
        }
      })

      // Calculate occupancy rate
      const occupancy = totalStorages > 0 ? (activeRentals / totalStorages) * 100 : 0

      // Calculate performance score (weighted average of occupancy and revenue per storage)
      const revenuePerStorage = totalStorages > 0 ? revenue / totalStorages : 0
      const avgRevenuePerStorage = locations.length > 0
        ? locations.reduce((sum, loc) => {
          const locStorages = storages.filter(s => s.location && s.location.id === loc.id)
          return sum + (locStorages.length > 0 ? 500 : 0) // Assume 500€ average monthly rent
        }, 0) / locations.length : 500

      const revenueScore = avgRevenuePerStorage > 0
        ? Math.min((revenuePerStorage / avgRevenuePerStorage) * 100, 200) : 0
      
      const performance = (occupancy * 0.6) + (revenueScore * 0.4)

      return {
        name: location.name || `Standort ${location.id}`,
        revenue: Math.round(revenue),
        occupancy,
        totalStorages,
        activeRentals,
        performance,
        location
      }
    })

    // Sort by performance descending
    locationStats.sort((a, b) => b.performance - a.performance)

    return {
      locations: locationStats,
      period,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    }
  }

  /**
   * Time period utility functions
   */
  const getDateRangeForPeriod = period => {
    const now = moment()
    let startDate
    const endDate = now

    switch (period) {
      case 'last3months':
        startDate = moment().subtract(3, 'months').startOf('month')
        break
      case 'last6months':
        startDate = moment().subtract(6, 'months').startOf('month')
        break
      case 'last12months':
        startDate = moment().subtract(12, 'months').startOf('month')
        break
      default: // current month
        startDate = moment().startOf('month')
        break
    }

    return {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      startDateMoment: startDate,
      endDateMoment: endDate
    }
  }

  /**
   * Reactive computed properties for Vue components
   */
  const reactiveMRR = () => ref(getCurrentMRR())
  const reactiveActiveRentalsCount = () => ref(getCurrentActiveRentalsCount())
  const reactiveAverageMonthlyRate = () => ref(getCurrentAverageMonthlyRate())

  return {
    // Core business logic functions
    isActiveRental,
    getActiveRentals,
    hasActiveRental,
    
    // MRR calculations
    calculateMRR,
    getCurrentMRR,
    calculateAverageMonthlyRate,
    getCurrentAverageMonthlyRate,
    
    // Count functions
    getActiveRentalsCount,
    getCurrentActiveRentalsCount,
    
    // Location-specific calculations
    calculateLocationMRR,
    
    // Revenue functions (from DashboardTiles.js)
    getRevenueTillNow,
    getCurrentMonthRevenue,
    getOrderItemsMonthlyRevenue,
    getSumOfAllActiveOrderItems,
    getSumOfAllActiveOrderItemsForCurrentMonth,
    getSumOfAllActiveOrderItemsTillToday,
    getSumOfAllActiveOrderItemsTillMoveoutdate,
    
    // Business intelligence functions (from DashboardTiles.js)
    getBusinessFunnelData,
    getStorageUtilizationTrends,
    getLocationPerformanceData,
    
    // Count functions (from DashboardTiles.js)
    getCountOfAllStorages,
    getCountOfAllOrders,
    getCountOfAllInquiries,
    getCountOfAllOffers,
    getCountOfAllOrderItems,
    
    // User functions
    getCurrentUser,
    
    // Inquiry functions
    getAcceptedInquiries,
    getOpenInquiries,
    
    // Offer functions
    getTotalSumOfAllStoragesAndOffers,
    getActiveOffers,
    
    // Storage availability functions
    getFreeStorages,
    getRentedStorages,
    
    // Utility functions
    getDateRangeForPeriod,
    
    // Reactive computed properties
    reactiveMRR,
    reactiveActiveRentalsCount,
    reactiveAverageMonthlyRate,
  }
}
